import React, { CSSProperties, useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";

import ReactDatePicker from "react-datepicker";
import styled from "styled-components";
import { scrollbars } from "theme/mixins";

import { useRootElement } from "ui/hooks";

import { Button } from "minimum-ui/core";
import { MenuWrapper } from "../Filter/Menu";
import CustomHeader from "./CustomHeader";
import DatePickerStyles from "./Styles";

export interface SingleMenuProps {
  open: boolean;
  onClose: () => void;
  onApply: (date: Date | null) => void;
  top: number;
  left: number;
  right: number;
  rightAligned: boolean;
  startDate: Date | null;
  translate: (
    key: string,
    options?: Record<string, string | number | boolean>
  ) => string;
}

const SingleMenu: React.FC<SingleMenuProps> = ({
  open,
  onClose,
  onApply,
  top,
  left,
  right,
  rightAligned = false,
  startDate = null,
  translate,
}) => {
  const portal = useRootElement("date-picker");

  const [startValue, setStartValue] = useState<Date | null>(startDate);
  const [datesChanged, setDatesChanged] = useState(false);

  // Keep our state in-sync with provided value
  useEffect(() => {
    setStartValue(startDate);
    setDatesChanged(false);
  }, [startDate]);

  const handleChange = useCallback((date: Date | null) => {
    setStartValue(date);
    if (date) {
      setDatesChanged(true);
    }
  }, []);

  const onCancel = useCallback(() => {
    onApply(null);
    setStartValue(null);
    setDatesChanged(false);
    onClose();
  }, [onApply, onClose]);

  const handleApply = useCallback(() => {
    onApply(startValue);
    onClose();
  }, [onApply, startValue, onClose]);

  const months = translate("months", {
    returnObjects: true,
    namespace: "DEFAULT",
  }) as unknown as string[];

  return createPortal(
    <>
      {open && <Backdrop onClick={onClose} />}
      <DatePicker
        $open={open}
        $right={rightAligned}
        $showReset={false}
        $showApply={false}
        role="dialog"
        style={
          {
            "--top": `${top}px`,
            "--left": `${left}px`,
            "--right": `${right}px`,
          } as CSSProperties
        }
      >
        <DatePickerStyles />
        <ReactDatePicker
          onChange={handleChange}
          selected={startValue}
          monthsShown={1}
          inline={true}
          startDate={startValue}
          calendarStartDay={1}
          fixedHeight={true}
          renderCustomHeader={CustomHeader(months)}
        />
        <ButtonContainer>
          <DatePickerButton
            size="large"
            variant="outlined"
            onClick={onCancel}
            disabled={!startValue}
          >
            Reset
          </DatePickerButton>
          <DatePickerButton
            size="large"
            variant="contained"
            disabled={!datesChanged}
            onClick={handleApply}
          >
            Apply
          </DatePickerButton>
        </ButtonContainer>
      </DatePicker>
    </>,
    portal
  );
};

const Backdrop = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  z-index: 100;
`;

const DatePicker = styled(MenuWrapper)`
  // width: 656px;
  padding: 0;
  overflow-y: auto;
  ${scrollbars()}
`;

const ButtonContainer = styled.div`
  margin: 0 16px 16px auto;
`;

const DatePickerButton = styled(Button)`
  margin-left: 16px;
`;

export default SingleMenu;
